import React, { useState, useEffect } from "react";
import axios from "axios";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import Button from "../Button";
import { useNavigate } from "react-router-dom";
import Callout from "../Callout";

function EditParentConsentForm() {
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState("");
  const [editedPdfUrl, setEditedPdfUrl] = useState("");
  const [isSigning, setIsSigning] = useState(false);
  const [originalBlob, setOriginalBlob] = useState(null);
  const navigate = useNavigate();

  const cachedUser = localStorage.getItem("cachedUser");
  const user = JSON.parse(cachedUser);
  const firstName = `${user.first_name}`;
  const lastName = `${user.last_name}`;
  const schoolName = `${user.school.name}`;
  const email = `${user.email}`;
  const dob = `${user.dob}`;
  const grade = `${user.grade}`;
  const currentYear = new Date().getFullYear();
  const numericGrade = parseInt(grade); // e.g. 8
  const graduationYear = currentYear + (12 - numericGrade);

  // Formated varialbes
  const formattedDob = `${dob.slice(0, 2)}/${dob.slice(2, 4)}/${dob.slice(4)}`;


  const prefillFields = async (pdfBlob) => {
    const existingPdfBytes = await pdfBlob.arrayBuffer(); // <– use the Blob directly
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
    const page = pdfDoc.getPages()[0];
  
    // Fill in the fields — adjust X/Y coordinates as needed
    page.drawText(firstName, { x: 250, y: 410, size: 12, font });
    page.drawText(lastName, { x: 80, y: 410, size: 12, font });
    page.drawText(email, { x: 80, y: 382, size: 12, font });
    page.drawText(schoolName, { x: 375, y: 377, size: 12, font });
    page.drawText(formattedDob, { x: 120, y: 342, size: 12, font });
    page.drawText(`${graduationYear}`, { x: 300, y: 345, size: 12, font });
    page.drawText(grade, { x: 500, y: 347, size: 12, font });
  
    const modifiedPdfBytes = await pdfDoc.save();
    const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
    const filledBlobUrl = URL.createObjectURL(blob);
    setPdfUrl(filledBlobUrl);
  };

  // Step 1: Load the PDF
  useEffect(() => {
    axios
      .get(`${API_ENDPOINT}/api/parent-consent/`, {
        headers: { Authorization: `Token ${localStorage.getItem("token")}` },
      })
      .then(async (response) => {
        const backendPath = response.data.pdf;
        const fullUrl = `${API_ENDPOINT}${backendPath}`;
        const blob = await fetch(fullUrl).then((res) => res.blob());
  
        // Save blob for later use
        const blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);
  
        // 👇 Save original blob too
        setOriginalBlob(blob);
      })
      .catch((error) => {
        console.error("Error fetching parent consent form:", error);
        setLoading(false);
      });
  }, [API_ENDPOINT]);

  // Step 2: Prefill fields after PDF is loaded
  useEffect(() => {
    if (!originalBlob) return;
    setLoading(true);
    prefillFields(originalBlob).then(() => setLoading(false));
  }, [originalBlob]);

  const handleSignDocument = async () => {
    if (!signature) {
      alert("Please provide your signature (your full name).");
      return;
    }
    setIsSigning(true);
    try {
      const now = new Date();
      const timestamp = now.toLocaleDateString();

      const existingPdfBytes = await fetch(pdfUrl).then((res) =>
        res.arrayBuffer()
      );
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const italicFont = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
      const firstPage = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);

      // Signature + timestamp coordinates
      firstPage.drawText(signature, {
        x: 152,
        y: 245,
        size: 14,
        font: italicFont,
        color: rgb(0, 0, 0),
      });
      firstPage.drawText(timestamp, {
        x: 302,
        y: 245,
        size: 12,
        font: font,
        color: rgb(0, 0, 0),
      });

      const editedPdfBytes = await pdfDoc.save();
      const blob = new Blob([editedPdfBytes], { type: "application/pdf" });
      const editedUrl = URL.createObjectURL(blob);
      setEditedPdfUrl(editedUrl);

      const formData = new FormData();
      formData.append("pdf", blob, "signed_parent_consent.pdf");

      await axios.put(`${API_ENDPOINT}/api/parent-consent/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Token ${localStorage.getItem("token")}`,
        },
      });

      navigate("/parent-form-success");
    } catch (error) {
      console.error("Error signing document:", error);
      alert("An error occurred while signing your document.");
    }
    setIsSigning(false);
  };

  if (loading) return <p>Loading your Parent Consent Form...</p>;

  return (
    <div style={{ padding: "40px", maxWidth: "1000px", margin: "0 auto", textAlign: "left" }}>
      <h1>Review and Sign Your Parent Consent Form</h1>
      <Callout text="Please review the document below. Once you have read and understood its contents, adopt your signature by confirming your full name in the field provided. When you click the Adopt and Sign button, your signature and the current date will be recorded automatically in the 'Student Signature' and 'Date' sections and a copy will automatically be sent to your parent/guardian for their required signature. Note: You will not be able to take Community College courses until your parent has signed this form." />

      {!editedPdfUrl && (
        <div style={{ marginTop: "20px" }}>
          <iframe
            src={pdfUrl}
            width="100%"
            height="1000px"
            title="Original PDF Preview"
            style={{ border: "1px solid #ccc" }}
          />
        </div>
      )}

      <div style={{ textAlign: "left", marginTop: "40px" }}>
        <h2 style={{ marginBottom: "10px" }}>Adopt Your Signature</h2>
        <p>Confirm your name and signature.</p>

        <label style={{ display: "block", marginBottom: "10px" }}>
          <strong>Full Name</strong>
        </label>
        <input
          type="text"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="Enter your full name"
          style={{
            width: "100%",
            padding: "10px",
            fontSize: "16px",
            marginBottom: "30px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />

        <label style={{ display: "block", marginBottom: "10px" }}>
          <strong>Preview Signature</strong>
        </label>
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "20px",
            marginBottom: "30px",
            minHeight: "140px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Times New Roman, serif",
            fontStyle: "italic",
            fontSize: "28px",
            backgroundColor: "#fafafa",
          }}
        >
          {signature || "Your signature preview"}
        </div>

        <div
          style={{
            padding: "20px",
            backgroundColor: "#f4f4f4",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        >
          <p style={{ fontSize: "14px", marginBottom: "20px" }}>
            By clicking Adopt and Sign, I agree that the signature will be the electronic representation of my signature for all purposes when I use it on documents, including legally binding contracts – just the same as a pen-and-paper signature.
          </p>
          <Button
            onClick={handleSignDocument}
            disabled={isSigning}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: isSigning ? "not-allowed" : "pointer",
            }}
          >
            {isSigning ? "Signing..." : "Adopt and Sign"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditParentConsentForm;